import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { hasResolutionMismatch } from "src/components/ResolutionMismatchWarning";
import type { StateType } from "src/redux/reducers";

import type { CanvasOption, CanvasOutputPickerProps } from "./types";

const getOutputLinkedCanvases = (rundownCanvases: RundownCanvas[]): RundownCanvas[] =>
	rundownCanvases.filter((canvas) => canvas.output?.id != null);

const mergeCanvasOptions = (
	outputLinkedCanvases: CanvasOption[],
	graphicCanvases: Partial<Canvas>[],
): CanvasOption[] => {
	const merged: CanvasOption[] = [...outputLinkedCanvases];
	for (const canvas of graphicCanvases) {
		if (canvas?.id != null && canvas.name != null && !merged.some(({ id }) => id === canvas.id)) {
			merged.push(canvas as CanvasOption);
		}
	}
	return merged;
};

const hasSameCanvasIds = (a: Canvas["id"][], b: Canvas["id"][]): boolean =>
	a.length === b.length && new Set(a).size === new Set([...a, ...b]).size;

const getButtonText = (canvases: Partial<Canvas>[]): string =>
	canvases.map((canvas) => canvas.name ?? "").join(", ");

type UseCanvasOutputPickerParams = CanvasOutputPickerProps;

type UseCanvasOutputPickerReturn = {
	options: CanvasOption[];
	selectedIds: Canvas["id"][];
	hasOverride: boolean;
	buttonText: string;
	handleToggle: (canvasId: Canvas["id"], checked: boolean) => void;
	handleReset: () => void;
};

export const useCanvasOutputPicker = (
	params: UseCanvasOutputPickerParams,
): UseCanvasOutputPickerReturn => {
	const { graphic, canvasOverride, onUpdate } = params;

	const defaultIds = useMemo<Canvas["id"][]>(
		() =>
			(graphic?.canvases ?? []).map((c) => c?.id).filter((id): id is Canvas["id"] => id != null),
		[graphic?.canvases],
	);

	const resolvedSelectedIds =
		!canvasOverride?.length || hasSameCanvasIds(canvasOverride, defaultIds)
			? defaultIds
			: canvasOverride;

	const [selectedIds, setSelectedIds] = useState<Canvas["id"][]>(resolvedSelectedIds);

	const rundownCanvases = useSelector(
		(state: StateType) => state.rundown.detail.data?.canvases ?? [],
	);

	const outputLinkedCanvases = useMemo(
		() => getOutputLinkedCanvases(rundownCanvases),
		[rundownCanvases],
	);

	const options = useMemo<CanvasOption[]>(() => {
		const merged = mergeCanvasOptions(outputLinkedCanvases, graphic?.canvases ?? []);
		if (!graphic) return merged;

		// Hide resolution-mismatched canvases unless already selected
		return merged.filter(
			(canvas) => selectedIds.includes(canvas.id) || !hasResolutionMismatch(graphic, canvas),
		);
	}, [outputLinkedCanvases, graphic, selectedIds]);

	const hasOverride = useMemo(
		() => !hasSameCanvasIds(selectedIds, defaultIds),
		[selectedIds, defaultIds],
	);

	const selectedCanvases = useMemo(
		() => options.filter((c) => c?.id != null && selectedIds.includes(c.id)),
		[options, selectedIds],
	);

	const buttonText = getButtonText(selectedCanvases);

	const handleToggle = useCallback(
		(canvasId: Canvas["id"], checked: boolean) => {
			// Guard against having less than 1 canvas override selected
			if (!checked && selectedIds.length <= 1 && selectedIds.includes(canvasId)) return;

			setSelectedIds((current) => {
				const next = checked ? [...current, canvasId] : current.filter((id) => id !== canvasId);
				onUpdate?.(next);
				return next;
			});
		},
		[onUpdate, selectedIds],
	);

	const handleReset = useCallback(() => {
		setSelectedIds(defaultIds);
		onUpdate?.([]);
	}, [defaultIds, onUpdate]);

	useEffect(() => {
		setSelectedIds(resolvedSelectedIds);
	}, [resolvedSelectedIds]);

	return {
		options,
		selectedIds,
		hasOverride,
		buttonText,
		handleToggle,
		handleReset,
	};
};
